/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Card } from "@material-ui/core";
import { getAllLexBotsV2, getAssociatedLexBotsV2FromConnect } from "../services/LexBotV2Service";
import { LexBotV2DisplayCard } from "../components/LexBotV2DisplayCard";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import {
	ContentLayout,
	LeftPanelLayout,
	PanelContainer,
	ProgressOverlay,
	RightPanelLayout,
} from "../../../components/Common";
import LexBotsList from "../components/LexBotsList";
const COMPONENT_NAME = "Admin lexBotManager/pages/LexBotPage";

/**
 * Page used to manage lex bots version 2.
 * Contains functionality to associate and disassociate lex bot to connect.
 */
const LexBotV2Page = () => {
	const [bots, setBots] = React.useState([]);
	const [initializePage, setInitializePage] = React.useState(true);
	const [loading, setLoading] = React.useState(true);
	const [botDetailsLoading, setBotDetailsLoading] = React.useState(true);
	const [selectedBot, setSelectedBot] = React.useState(undefined);
	const [selectedBotId, setSelectedBotId] = React.useState("");
	const [associatedFilter, setAssociatedFilter] = React.useState(false);
	const [associatedAliases, setAssociatedAliases] = React.useState([]);

	React.useEffect(() => {
		const fetchBots = async () => {
			try {
				let promises = [getAllLexBotsV2(), getAssociatedLexBotsV2FromConnect()];
				promises = await Promise.all(promises);
				let lexBots = promises[0];
				let associatedLexBots = promises[1];
				let associatedAliases = [];
				if (associatedLexBots && associatedLexBots.length > 0) {
					for (let x = 0; x < lexBots.length; x++) {
						const associated = associatedLexBots.filter((bot) => bot.LexV2Bot.AliasArn.indexOf(lexBots[x].botId) > -1);
						for (let y = 0; y < associated.length; y++) {
							if (associated[y].LexV2Bot.AliasArn.indexOf(lexBots[x].botId) > -1) {
								lexBots[x] = { ...lexBots[x], associated: true };
								const alias = associated[y].LexV2Bot.AliasArn;
								const aliasId = alias.substring(alias.lastIndexOf("/") + 1, alias.length);
								associatedAliases.push({ botId: lexBots[x].botId, aliasId: aliasId });
							}
						}
					}
				}
				setAssociatedAliases(associatedAliases);
				setBots(lexBots);
				const defaultBot = lexBots.length > 0 ? lexBots[0] : undefined;
				setSelectedBotId(defaultBot?.botId || "");
				setSelectedBot(defaultBot);
			} catch (error) {
				v2.error(`Error fetching lex bot list`, {
					additionalData: {
						error: errorToObject(error),
					},
					logCode: errorCodes.GET_LEX_BOT_V2_LIST_ERROR_API_ERROR,
					componentName: COMPONENT_NAME,
				});
			} finally {
				setLoading(false);
			}
		};
		if (initializePage) {
			fetchBots();
			setLoading(true);
			setInitializePage(false);
		}
	}, [initializePage]);

	const [filterBotName, setFilterBotName] = React.useState("");
	const searchMatches =
		(bots && bots?.filter((x) => x.botName.includes(filterBotName) && (!associatedFilter || x.associated))) || [];

	// Renders the Lex Bot Details Panel.
	const lexBotDetails = React.useMemo(() => {
		const updateLocalLexBot = (newLexBot, index) => {
			let newBots = bots.map((x) => (x.botId === index ? newLexBot : x));
			setBots(newBots);
			setSelectedBot(newLexBot);
		};
		const updateAliases = (newLexBotAlias) => {
			setAssociatedAliases(newLexBotAlias);
		};
		return (
			<LexBotV2DisplayCard
				originalLexBot={selectedBot}
				updateLocalLexBot={(newLexBot) => updateLocalLexBot(newLexBot, selectedBotId)}
				setBotDetailsLoading={setBotDetailsLoading}
				selectedBotId={selectedBotId}
				associatedAliases={associatedAliases}
				updateAliases={(newLexBot) => updateAliases(newLexBot)}
				botDetailsLoading={botDetailsLoading}
			/>
		);
	}, [selectedBot, selectedBotId, bots, associatedAliases, botDetailsLoading]);

	return (
		<ContentLayout>
			{loading && <ProgressOverlay />}
			<PanelContainer>
				<LeftPanelLayout>
					<Card style={{ minHeight: 600, height: "100%", padding: "10px 5px" }}>
						<LexBotsList
							associatedFilter={associatedFilter}
							botDetailsLoading={botDetailsLoading}
							filterBotName={filterBotName}
							setAssociatedFilter={setAssociatedFilter}
							searchMatches={searchMatches}
							setFilterBotName={setFilterBotName}
							selectedBotId={selectedBotId}
							setSelectedBot={setSelectedBot}
							setSelectedBotId={setSelectedBotId}
						/>
					</Card>
				</LeftPanelLayout>
				<RightPanelLayout>{lexBotDetails}</RightPanelLayout>
			</PanelContainer>
		</ContentLayout>
	);
};

export default LexBotV2Page;
